<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent>
      <template #flashMessage>
        <EduWarningFlashMessage :email="email" />
      </template>
      <h4 class="my-6">
        Create an Account
      </h4>
      <div class="social-login-btns mb-4">
        <DpplGoogleLinkedinButton
          :href="googleUrl"
          class="google-linkedin-button"
        >
          <img
            src="@/assets/icon-google.svg"
            width="64px"
            height="20px"
            alt="Authenticate with Google"
          >
        </DpplGoogleLinkedinButton>
        <DpplGoogleLinkedinButton
          :href="microsoftUrl"
          class="google-linkedin-button ml-4"
        >
          <img
            class="ml-1 mr-3"
            src="@/assets/icon-microsoft.svg"
            alt="Sign in with Microsoft"
          >
          Sign-in with Microsoft
        </DpplGoogleLinkedinButton>
      </div>
      <SsoLoginButton
        class="mb-6 ml-4 w-100"
        :is-active="singleSignOnMode"
        @toggle-sso="singleSignOnMode = !singleSignOnMode"
      />
      <DpplPageBreak
        text="OR"
        class="mb-4"
      />
      <v-form
        ref="form"
        v-model="valid"
        class="w-100"
        @submit.prevent="submitForm"
      >
        <div
          v-if="!singleSignOnMode"
          class="flex-grow-1 d-flex align-center justify-space-between"
        >
          <DpplSignupFormHalfTextField
            v-model="firstName"
            label="First Name"
            :rules="firstNameRules"
            validate-on="blur"
          />
          <DpplSignupFormHalfTextField
            v-model="lastName"
            label="Last Name"
            :rules="lastNameRules"
            validate-on="blur"
            margin="0px 0px 0px 24px"
          />
        </div>
        <DpplTextField
          v-model="email"
          label="Company Email"
          :rules="emailRules"
          validate-on="blur"
          @change="trimEmail"
        />
        <DpplTextField
          v-if="!singleSignOnMode"
          v-model="password"
          class="mb-3"
          label="Password"
          :rules="passwordRules"
          type="password"
          autocomplete="off"
          validate-on="blur"
          data-sentry-mask
        />
        <DpplSignupFormButton
          class="mb-4"
          type="submit"
          block
          :loading="loading"
        >
          Continue
        </DpplSignupFormButton>
      </v-form>
      <WizardFooter>
        <h5>
          Already have an account?
          <router-link
            class="pl-2"
            to="login"
          >
            Sign In
          </router-link>
        </h5>
      </WizardFooter>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import WizardFooter from '@/components/shared/WizardFooter.vue';
import { signupBuildAuthUrls, signupCreateAccount } from '@/api/signup';
import { redirectToSsoProvider } from '@/helpers/redirect';
import validation from '@/helpers/validation';
import DpplGoogleLinkedinButton from '@/components/datapeople/DpplGoogleLinkedinButton.vue';
import DpplSignupFormHalfTextField from '@/components/datapeople/DpplSignupFormHalfTextField.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import DpplPageBreak from '@/components/datapeople/DpplPageBreak.vue';
import { mapActions } from 'pinia';
import { useWizard } from '@/stores/wizard/wizard';
import SsoLoginButton from '@/components/signup/SsoLoginButton.vue';
import EduWarningFlashMessage from '../components/shared/EduWarningFlashMessage.vue';

export default {
  name: 'SignupView',
  components: {
    WizardContainer,
    WizardContent,
    WizardFooter,
    DpplGoogleLinkedinButton,
    DpplSignupFormHalfTextField,
    DpplTextField,
    DpplSignupFormButton,
    DpplPageBreak,
    EduWarningFlashMessage,
    SsoLoginButton,
  },
  emits: ['track-page-load'],
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      firstNameRules: validation.firstNameRules(),
      lastNameRules: validation.lastNameRules(),
      emailRules: validation.emailRules(),
      passwordRules: validation.passwordRules(),
      valid: false,
      loading: false,
      googleUrl: null,
      microsoftUrl: null,
      singleSignOnMode: false,
    };
  },
  head() {
    return {
      title: this.$route.meta.title || 'Datapeople',
      link: [
        { rel: 'canonical', href: 'https://app.datapeople.io/' },
      ],
      meta: [
        {
          name: 'description',
          content: 'Create an account to begin writing job descriptions that are thoughtful, concise, and welcoming to candidates from every background.',
        },
        {
          name: 'robots',
          content: 'max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        {
          name: 'og:locale',
          content: 'en_US',
        },
        {
          name: 'og:type',
          content: 'website',
        },
        {
          name: 'og:title',
          content: 'Create an Account - Datapeople',
        },
        {
          name: 'og:description',
          content: 'Create an account to begin writing job descriptions that are thoughtful, concise, and welcoming to candidates from every background.',
        },
        {
          name: 'og:url',
          content: 'https://app.datapeople.io/',
        },
        {
          name: 'og:site_name',
          content: 'Datapeople',
        },
        {
          name: 'og:image',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          name: 'og:image:secure_url',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          name: 'og:image:width',
          content: '1200',
        },
        {
          name: 'og:image:height',
          content: '630',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:description',
          content: 'Create an account to begin writing job descriptions that are thoughtful, concise, and welcoming to candidates from every background.',
        },
        {
          name: 'twitter:title',
          content: 'Create an Account - Datapeople',
        },
        {
          name: 'twitter:site',
          content: '@Datapeopleio',
        },
        {
          name: 'twitter:image',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          name: 'twitter:creator',
          content: '@Datapeopleio',
        },
      ],
    };
  },
  async created() {
    const urlFlashMessage = this.$route.query?.flash_message || '';
    const urlFlashColor = this.$route.query?.flash_color;
    if (urlFlashMessage || urlFlashColor) {
      this.updateFlash({ message: urlFlashMessage, status: urlFlashColor });
    }

    const getLinkedinAndGoogleOauthUrls = async () => {
      const responseJSON = await signupBuildAuthUrls();
      if (responseJSON.success === true) {
        this.googleUrl = responseJSON.google_auth_url;
        this.microsoftUrl = responseJSON.microsoft_auth_url;
      }
    };

    Promise.all([getLinkedinAndGoogleOauthUrls()]).catch((e) => {
      this.updateFlash({ message: e.message });
      this.loading = false;
    });
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Sign Up' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupCreateAccount({
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password,
          });
          if (responseJSON.success === true) {
            this.$router.push({
              name: 'verify-email',
            });
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Create account error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        const { errors } = responseJSON;
        if (errors && errors.sso_mandatory) {
          localStorage.setItem('lastSso', JSON.stringify({
            provider: errors.provider,
            name: errors.company_name,
            isGoogle: errors.is_google,
            isMicrosoft: errors.is_microsoft,
          }));

          const { from = '' } = this.$route.query;
          redirectToSsoProvider({
            from,
            provider: errors.provider,
            isGoogle: errors.is_google,
            isMicrosoft: errors.is_microsoft,
          });
          return;
        }
        // if response contains please use corporate email error and no errors in other fields
        if ( // If only error is please include corporate email
          responseJSON.errors
          && Object.keys(responseJSON.errors).length === 1
          && responseJSON.errors.email
          && responseJSON.errors.email.includes('Please use your corporate email address.')
        ) {
          this.$router.push({
            name: 'provide-work-email',
            params: {
              firstName: this.firstName,
              lastName: this.lastName,
              password: this.password,
            },
          });
        } else if (
          responseJSON.errors
          && responseJSON.errors.email
          && responseJSON.errors.email
            .includes('Please sign up with your individual work email address.')
        ) {
          this.updateFlash({ message: 'Please sign up with your individual work email address.' });
          return;
        } else if (
          // if response contains please use corporate email error and no errors in other fields
          responseJSON.errors
          && responseJSON.errors.auth
          && responseJSON.errors.auth.includes('user already exists.')
        ) {
          this.updateFlash({ template: 'user-exists' });
          return;
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
    trimEmail() {
      this.email = validation.trimSpaces(this.email);
    },
  },
};
</script>

<style scoped lang="less">
.social-login-btns {
  display: flex;
  justify-content: center;
  width: 100%;
}

.google-linkedin-button {
  text-transform: initial;
}

.row {
  margin-left: 0;
  margin-right: 0;
}
</style>
